import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Search, X } from "lucide-react";

/**
 * Type-to-filter multi-select for the areas-of-interest field.
 *
 * Replaces a wall of sixteen always-visible chips. That grid pushed the rest of
 * the step below the fold on a laptop and well below it on a phone, so the
 * strongest signal we ask a new member for was also the easiest to skim past. One
 * input plus the chosen tags is a fixed two rows until they start selecting.
 *
 * Built on the ARIA combobox pattern rather than a styled `<div>` list, because a
 * filter that cannot be driven from the keyboard just moves the problem: the
 * previous chips were at least focusable buttons.
 */
export default function InterestTypeahead({
  options,
  selected,
  onToggle,
  invalid = false,
  required = false,
}: {
  options: readonly string[];
  selected: readonly string[];
  onToggle: (value: string) => void;
  /** Colours the border when the surrounding field is showing an error. */
  invalid?: boolean;
  /**
   * Marks the control required for assistive technology. The visible `*` comes
   * from the surrounding Field; without this the requirement is only visual, so
   * a screen-reader user meets the validation error instead of the expectation.
   */
  required?: boolean;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  /*
   * The list is portalled to a fixed position rather than absolutely positioned
   * in the field. Inside the member edit drawer the body scrolls, and a scroll
   * container clips an absolutely positioned child — so the suggestions were cut
   * off by the drawer instead of floating over the tags beneath them.
   */
  const [listPosition, setListPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listboxId = useId();

  /*
   * Every topic stays in the list, with the chosen ones checked.
   *
   * They used to be filtered out, on the reasoning that the tags below already
   * record what is selected. In practice that meant the list answered "what is
   * left" while the question being asked is "what did I pick" — and a topic
   * silently vanishing when you chose it reads as the control losing it. Showing
   * the full list with checkmarks answers both at once, and makes the list a
   * place to deselect as well as select.
   */
  const matches = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    return options.filter(option => !needle || option.toLocaleLowerCase().includes(needle));
  }, [options, query]);

  // Keep the highlight inside the list as filtering shrinks it, or Enter selects
  // whatever happens to sit at a stale index.
  useEffect(() => {
    setActiveIndex(current => (current >= matches.length ? Math.max(0, matches.length - 1) : current));
  }, [matches.length]);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const field = fieldRef.current?.getBoundingClientRect();
      if (!field) return;
      setListPosition({ top: field.bottom + 6, left: field.left, width: field.width });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handlePointerDown(event: MouseEvent | TouchEvent) {
      const target = event.target as Node;
      if (wrapperRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [open]);

  function commit(option: string) {
    // A toggle, not an add: the list shows what is already chosen, so the
    // obvious thing to do with a checked row is uncheck it.
    onToggle(option);
    // Query cleared so the next topic starts from the full list.
    setQuery("");
    setActiveIndex(0);
    setOpen(true);
    inputRef.current?.focus();
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (matches.length === 0) return;
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex(current => (current + step + matches.length) % matches.length);
      return;
    }

    if (event.key === "Enter") {
      // Only when a suggestion is highlighted — otherwise Enter belongs to the
      // form, and a stray keystroke here would submit the step.
      if (open && matches[activeIndex]) {
        event.preventDefault();
        commit(matches[activeIndex]);
      }
      return;
    }

    if (event.key === "Escape") {
      setOpen(false);
      return;
    }

    // Backspace on an empty query removes the last tag, which is what a tag input
    // trains people to expect.
    if (event.key === "Backspace" && !query && selected.length > 0) {
      onToggle(selected[selected.length - 1]);
    }
  }

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      {/*
        The whole box is the control, not just the text cursor's width of it.

        Only the input opened the list, so clicking the field's padding, its search
        icon, or the space beside a chosen tag did nothing — which reads as a field
        with no topics in it rather than a field that was never focused.
      */}
      <div
        ref={fieldRef}
        onMouseDown={event => {
          if (event.target instanceof HTMLElement && event.target.closest("button, input")) return;
          event.preventDefault();
          inputRef.current?.focus();
          setOpen(true);
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: ".5rem",
          padding: ".7rem .85rem",
          cursor: "text",
          border: `1px solid ${invalid ? "rgba(244,63,94,.75)" : open ? "rgba(233,30,140,.5)" : "rgba(255,255,255,.1)"}`,
          borderRadius: ".625rem",
          background: "rgba(255,255,255,.04)",
          boxShadow: open ? "0 0 0 3px rgba(233,30,140,.1)" : "none",
          transition: "border-color .2s ease, box-shadow .2s ease",
        }}
      >
        <Search size={15} aria-hidden="true" style={{ flexShrink: 0, color: open ? "#E91E8C" : "#475569", transition: "color .2s" }} />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={open && matches[activeIndex] ? `${listboxId}-${activeIndex}` : undefined}
          aria-label="Areas of Interest"
          aria-required={required || undefined}
          aria-invalid={invalid || undefined}
          value={query}
          placeholder={selected.length ? "Add another topic…" : "Start typing to find topics…"}
          onChange={event => { setQuery(event.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          style={{
            width: "100%",
            border: 0,
            background: "transparent",
            color: "#FFF",
            outline: "none",
            fontFamily: "'Inter', sans-serif",
            fontSize: ".9rem",
            minWidth: 0,
          }}
        />
      </div>

      {open && createPortal(
        <ul
          id={listboxId}
          ref={listRef}
          role="listbox"
          aria-label="Areas of interest"
          aria-multiselectable="true"
          style={{
            position: "fixed",
            zIndex: 150,
            top: listPosition?.top ?? -9999,
            left: listPosition?.left ?? -9999,
            width: listPosition?.width ?? 0,
            // Caps the dropdown instead of the page: the list scrolls, the form
            // does not grow, which is the whole point of this control.
            maxHeight: "13.5rem",
            overflowY: "auto",
            margin: 0,
            padding: ".3rem",
            listStyle: "none",
            borderRadius: ".625rem",
            border: "1px solid rgba(255,255,255,.12)",
            background: "#101728",
            boxShadow: "0 18px 48px rgba(0,0,0,.5)",
          }}
        >
          {matches.length === 0 ? (
            <li style={{ padding: ".6rem .65rem", color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: ".8rem" }}>
              No topics match “{query.trim()}”.
            </li>
          ) : (
            matches.map((option, index) => {
              const isSelected = selected.includes(option);
              const isActive = index === activeIndex;
              return (
                <li key={option} role="none">
                  <button
                    id={`${listboxId}-${index}`}
                    type="button"
                    role="option"
                    /* Selection state, not the highlight — the highlight is carried
                       by aria-activedescendant on the input. These were conflated
                       when the list could only ever contain unselected topics. */
                    aria-selected={isSelected}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => commit(option)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: ".5rem",
                      width: "100%",
                      padding: ".55rem .65rem",
                      border: 0,
                      borderRadius: ".45rem",
                      background: isActive ? "rgba(233,30,140,.14)" : "transparent",
                      color: isSelected ? "#FBCFE8" : isActive ? "#FFF" : "#CBD5E1",
                      cursor: "pointer",
                      fontFamily: "'Inter', sans-serif",
                      fontSize: ".84rem",
                      textAlign: "left",
                    }}
                  >
                    {/*
                      A box that is empty or ticked, rather than an icon that
                      fades in on hover. The point is to be able to read which
                      topics are already chosen without moving the pointer.
                    */}
                    <span
                      aria-hidden="true"
                      style={{
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        width: 16,
                        height: 16,
                        borderRadius: ".3rem",
                        border: `1px solid ${isSelected ? "rgba(233,30,140,.7)" : "rgba(255,255,255,.22)"}`,
                        background: isSelected ? "rgba(233,30,140,.35)" : "transparent",
                        color: "#FFF",
                      }}
                    >
                      {isSelected && <Check size={11} />}
                    </span>
                    {option}
                  </button>
                </li>
              );
            })
          )}
        </ul>,
        document.body,
      )}

      {selected.length > 0 && (
        <ul
          aria-label="Selected areas of interest"
          style={{ display: "flex", flexWrap: "wrap", gap: ".4rem", margin: ".65rem 0 0", padding: 0, listStyle: "none" }}
        >
          {selected.map(option => (
            <li key={option}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: ".4rem",
                  padding: ".3rem .3rem .3rem .65rem",
                  borderRadius: "999px",
                  border: "1px solid rgba(233,30,140,.35)",
                  background: "rgba(233,30,140,.12)",
                  color: "#FBCFE8",
                  fontFamily: "'Inter', sans-serif",
                  fontSize: ".76rem",
                }}
              >
                {option}
                <button
                  type="button"
                  onClick={() => onToggle(option)}
                  aria-label={`Remove ${option}`}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 18,
                    height: 18,
                    padding: 0,
                    border: 0,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,.08)",
                    color: "#FBCFE8",
                    cursor: "pointer",
                  }}
                >
                  <X size={11} aria-hidden="true" />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
